import jsPDF from 'jspdf'
import { ClinicalHistory } from '../types'

function formatDateForPdf(date: Date | string): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Helper to load an image as base64 for jsPDF
async function loadImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

export async function generateClinicalHistoryPdf(
  history: ClinicalHistory,
  patientName?: string,
) {
  const doc = new jsPDF('p', 'mm', 'a4')
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 18
  const contentWidth = pageWidth - margin * 2
  let y = margin

  const colors = {
    primary: [0, 120, 150] as [number, number, number],
    dark: [30, 30, 30] as [number, number, number],
    gray: [100, 100, 100] as [number, number, number],
    lightGray: [160, 160, 160] as [number, number, number],
    bg: [245, 248, 250] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    line: [200, 210, 215] as [number, number, number],
    accent: [0, 150, 180] as [number, number, number],
  }

  // ========== LOAD LOGO ==========
  const logoUrl =
    'https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg'
  const logoBase64 = await loadImageAsBase64(logoUrl)

  // ========== REUSABLE HEADER FUNCTION ==========
  function drawPageHeader() {
    // Top accent bar
    doc.setFillColor(...colors.primary)
    doc.rect(0, 0, pageWidth, 3, 'F')

    y = 10

    // Logo on the left
    const logoSize = 22
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', margin, y - 2, logoSize, logoSize)
      } catch (e) {
        // If image fails, skip silently
      }
    }

    // Title block - next to logo
    const titleX = logoBase64 ? margin + logoSize + 5 : margin
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(20)
    doc.setTextColor(...colors.primary)
    doc.text('HISTORIA CLÍNICA', titleX, y + 6)

    // "Savidna" brand text below title
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...colors.accent)
    doc.text('Savidna - Centro de Bienestar y Belleza', titleX, y + 13)

    // Date on the right
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...colors.lightGray)
    doc.text('Fecha:', pageWidth - margin - 40, y + 4)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9.5)
    doc.setTextColor(...colors.dark)
    doc.text(formatDateForPdf(history.date), pageWidth - margin - 40, y + 9)

    y += 26

    // Divider
    doc.setDrawColor(...colors.primary)
    doc.setLineWidth(0.8)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6
  }

  function checkPageBreak(needed: number) {
    if (y + needed > pageHeight - 25) {
      doc.addPage()
      drawPageHeader()
      return true
    }
    return false
  }

  function drawSectionTitle(title: string) {
    checkPageBreak(14)
    y += 4
    doc.setFillColor(...colors.primary)
    doc.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10.5)
    doc.setTextColor(...colors.white)
    doc.text(title.toUpperCase(), margin + 4, y + 6.2)
    y += 13
    doc.setTextColor(...colors.dark)
  }

  function drawField(label: string, value: string, fullWidth = false) {
    checkPageBreak(10)
    const fieldWidth = fullWidth ? contentWidth : contentWidth / 2 - 2
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...colors.gray)
    doc.text(label, currentX, y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    doc.setTextColor(...colors.dark)
    const lines = doc.splitTextToSize(value || '-', fieldWidth - 2)
    doc.text(lines, currentX, y + 4.5)
    return 4.5 + lines.length * 4
  }

  let currentX = margin

  function drawFieldPair(
    label1: string,
    value1: string,
    label2: string,
    value2: string,
  ) {
    checkPageBreak(14)
    const halfWidth = contentWidth / 2 - 4

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...colors.gray)
    doc.text(label1, margin, y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    doc.setTextColor(...colors.dark)
    const lines1 = doc.splitTextToSize(value1 || '-', halfWidth)
    doc.text(lines1, margin, y + 4.5)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...colors.gray)
    doc.text(label2, margin + contentWidth / 2 + 2, y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    doc.setTextColor(...colors.dark)
    const lines2 = doc.splitTextToSize(value2 || '-', halfWidth)
    doc.text(lines2, margin + contentWidth / 2 + 2, y + 4.5)

    y += 4.5 + Math.max(lines1.length, lines2.length) * 4 + 2
  }

  function drawTextBlock(label: string, value: string) {
    checkPageBreak(16)
    if (label) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(...colors.gray)
      doc.text(label, margin, y)
      y += 4.5
    }
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9.5)
    doc.setTextColor(...colors.dark)
    const lines = doc.splitTextToSize(value || '-', contentWidth - 4)
    for (const line of lines) {
      checkPageBreak(5)
      doc.text(line, margin + 1, y)
      y += 4.2
    }
    y += 2
  }

  // ========== PAGE 1: HEADER ==========
  drawPageHeader()

  // ========== PATIENT INFO BOX ==========
  doc.setFillColor(...colors.bg)
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'F')
  doc.setDrawColor(...colors.line)
  doc.setLineWidth(0.3)
  doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'S')

  const pb = history.personalBackground
  const boxY = y + 5

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...colors.gray)
  doc.text('Paciente:', margin + 5, boxY)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(...colors.dark)
  doc.text(pb.fullName || patientName || '-', margin + 28, boxY)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...colors.gray)

  doc.text('Edad:', margin + 5, boxY + 7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  doc.text(`${pb.age} años`, margin + 20, boxY + 7)

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.gray)
  doc.text('Sexo:', margin + 50, boxY + 7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  doc.text(pb.gender || '-', margin + 65, boxY + 7)

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.gray)
  doc.text('Teléfono:', margin + 100, boxY + 7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  doc.text(pb.phone || '-', margin + 122, boxY + 7)

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.gray)
  doc.text('Email:', margin + 5, boxY + 14)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  doc.text(pb.email || '-', margin + 22, boxY + 14)

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.gray)
  doc.text('Dirección:', margin + 100, boxY + 14)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  const addrLines = doc.splitTextToSize(pb.address || '-', 55)
  doc.text(addrLines[0] || '-', margin + 122, boxY + 14)

  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...colors.gray)
  doc.text('Doctor:', margin + 5, boxY + 21)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...colors.dark)
  doc.text(`Dr. ${history.doctorName}`, margin + 24, boxY + 21)

  y += 34

  // ========== ANTECEDENTES PERSONALES ==========
  drawSectionTitle('Antecedentes Personales')

  drawFieldPair(
    'Ocupación:',
    pb.occupation,
    'N° de Hijos:',
    String(pb.numberOfChildren),
  )
  drawFieldPair(
    'Tipo de Parto:',
    pb.deliveryType,
    'F. Nacimiento:',
    pb.birthDate || '-',
  )
  drawFieldPair('Alcohol:', pb.alcohol, 'Tabaco:', pb.tobacco)
  drawFieldPair('Drogas:', pb.drugs, 'Infusiones:', pb.infusions)
  drawTextBlock('Motivo de la Consulta:', pb.consultationReason)

  // ========== ANTECEDENTES CLÍNICOS ==========
  const cb = history.clinicalBackground
  drawSectionTitle('Antecedentes Clínicos')

  drawTextBlock('Enfermedades:', cb.diseases)
  drawTextBlock('Medicamentos Actuales:', cb.currentMedications)
  drawTextBlock('Enfermedades Crónicas:', cb.chronicDiseases)
  drawTextBlock('Antecedentes Quirúrgicos:', cb.surgicalHistory)
  drawFieldPair(
    'Implantes/Dispositivos:',
    cb.implantsOrDevices,
    'Tipo:',
    cb.implantType,
  )
  drawTextBlock('Alergias:', cb.allergies)

  // ========== DIAGNÓSTICO ==========
  drawSectionTitle('Diagnóstico')
  drawTextBlock('', history.diagnosis)

  // ========== FORZAR PÁGINA 2 PARA TRATAMIENTO ==========
  doc.addPage()
  drawPageHeader()

  // ========== TRATAMIENTO (PÁGINA 2) ==========
  drawSectionTitle('Tratamiento')
  drawTextBlock('', history.treatment)

  // ========== INDICACIONES MÉDICAS ==========
  drawSectionTitle('Indicaciones Médicas')
  drawTextBlock('', history.medicalIndications || '-')

  // ========== SESIONES ==========
  drawSectionTitle('Sesiones')
  if (history.sessions || history.sessionNumber) {
    drawFieldPair(
      'Sesiones:',
      history.sessions,
      'N° de Sesión:',
      history.sessionNumber,
    )
  } else {
    drawTextBlock('', '-')
  }

  // ========== COMENTARIOS ==========
  drawSectionTitle('Comentarios')
  drawTextBlock('', history.comments || '-')

  // ========== FIRMA - FORZAR AL FINAL DE LA PÁGINA ==========
  const signatureBlockHeight = 45
  const signatureY = pageHeight - 25 - signatureBlockHeight

  if (y < signatureY) {
    y = signatureY
  }

  // Separator line before signature
  doc.setDrawColor(...colors.line)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageWidth - margin, y)
  y += 15

  // Signature line
  const sigX = pageWidth / 2
  doc.setDrawColor(...colors.dark)
  doc.setLineWidth(0.5)
  doc.line(sigX - 35, y, sigX + 35, y)
  y += 5
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(...colors.dark)
  doc.text(history.doctorSignature || history.doctorName, sigX, y, {
    align: 'center',
  })
  y += 5
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  doc.setTextColor(...colors.gray)
  doc.text('Firma del Dr. (a)', sigX, y, { align: 'center' })
  y += 4
  doc.setFontSize(7.5)
  doc.text(formatDateForPdf(history.date), sigX, y, { align: 'center' })

  // ========== FOOTER on all pages ==========
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)

    // Bottom accent bar
    doc.setFillColor(...colors.primary)
    doc.rect(0, pageHeight - 3, pageWidth, 3, 'F')
    // Page number
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(...colors.lightGray)
    doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 6, {
      align: 'center',
    })
    doc.text('Savidna - Historia Clínica Confidencial', margin, pageHeight - 6)
    doc.text(
      formatDateForPdf(history.date),
      pageWidth - margin,
      pageHeight - 6,
      { align: 'right' },
    )
  }

  // Download
  const fileName = `Historia_Clinica_${(pb.fullName || patientName || 'paciente').replace(/\s+/g, '_')}_${new Date(history.date).toISOString().split('T')[0]}.pdf`
  doc.save(fileName)
}
